import Add from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import { type GridSortModel } from "@mui/x-data-grid-premium";
import { CrossTenantEmailIntakeSendersQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useLocalOmnisearch } from "hooks/omnisearch-v2";
import { useCallback, useMemo } from "react";

import { CROSS_TENANT_SENDERS_QUERY } from "./inboundQuery";
import { SectionHeading } from "./SectionHeading";
import { SENDERS_EXCLUDE_FILTER_COLUMNS, useSenderColumns } from "./sendersGridColumns";

type SenderListingRow =
  CrossTenantEmailIntakeSendersQuery["emailIntakeSenders"]["items"][number] & { id: string };

const INITIAL_SORT: GridSortModel = [{ field: "tenant", sort: "asc" }];

/** Matches the inbound grid; see the note there on why the footer depends on this. */
const INITIAL_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

/** The audit trail is here to be looked up, not read every time. */
const INITIAL_VISIBILITY = {
  createdBy: false,
  updatedBy: false,
  updatedAt: false,
  // Looked up, not scanned. Always-on columns are what push the useful ones off the
  // right edge behind a scrollbar that reveals nothing.
  documentCount: false,
  // A hint for organizations with several vendor mailboxes; most have one.
  facility: false,
};

/**
 * The V2 grid floors its scroller at 300px for "consistent layout", which suits its
 * own default of 25 rows. At five that leaves a band of blank white above the footer.
 * Let the rows size it, and keep a smaller floor for the empty-state message so a
 * grid with nothing in it is not a tall white box.
 */
const GRID_SX = {
  "& .MuiDataGrid-virtualScroller": { minHeight: 0 },
  "& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner": { minHeight: 120 },
};

const getItems = (data: CrossTenantEmailIntakeSendersQuery): SenderListingRow[] =>
  data.emailIntakeSenders.items.map((item) => ({ ...item, id: item.sender.id }));
const getCount = (data: CrossTenantEmailIntakeSendersQuery) => data.emailIntakeSenders.count;

function toSortInput(model: GridSortModel) {
  return model
    .filter((entry) => entry.sort)
    .map((entry) => ({ field: entry.field, sort: entry.sort === "asc" ? "ASC" : "DESC" }));
}

/**
 * Every allowlisted sender, across every organization.
 *
 * One grid rather than a page per customer, because the set is small by nature: an
 * organization needs one entry per vendor mailbox, and the whole environment holds a
 * handful. Seeing them together is also the only way to notice a collision — two
 * organizations claiming the same value stops the mail for both, which was previously
 * visible only in the add dialog or after a refusal.
 */
export function SendersGrid({
  onAdd,
  onEdit,
  onToggle,
  onDelete,
}: {
  onAdd: () => void;
  onEdit: (row: SenderListingRow) => void;
  onToggle: (row: SenderListingRow, enabled: boolean) => void;
  onDelete: (row: SenderListingRow) => void;
}) {
  // Component-local rather than URL-driven, because omnisearch has no URL param
  // namespacing: two URL-driven grids on one page read and write the same `omnisearch`
  // term, so this box showed the inbound grid's filter and typing in either one
  // changed the other. The inbound grid keeps the URL, since that is the view worth
  // linking to.
  const omnisearchSource = useLocalOmnisearch();
  const columns = useSenderColumns({ onEdit, onToggle, onDelete });

  // In the grid's own command strip rather than up beside the heading, so every
  // control that acts on this table sits in one row with Filters and Manage Columns.
  const commandButtons = useMemo(
    () => [
      <Button key="add-sender" size="small" variant="outlined" startIcon={<Add />} onClick={onAdd}>
        Add sender
      </Button>,
    ],
    [onAdd]
  );

  const buildVariables = useCallback(
    (search: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toSortInput(sort),
    }),
    []
  );

  return (
    <Stack spacing={1.5}>
      <SectionHeading
        title="Allowed Senders"
        description="Every sender allowlisted to email manifests across all organizations. A sender only reaches one organization, so when two of them allowlist the same address or domain there is no way to tell whose mail it is, so it is held for a person to route — the Conflicts column is where those show up."
      />
      <OmnisearchDataGridV2
        persistenceKey="manifest-email-senders"
        omnisearchSource={omnisearchSource}
        commandButtons={commandButtons}
        excludeFilterColumns={SENDERS_EXCLUDE_FILTER_COLUMNS}
        query={CROSS_TENANT_SENDERS_QUERY}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        columns={columns}
        initialSortModel={INITIAL_SORT}
        initialPageSize={INITIAL_PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialColumnVisibilityModel={INITIAL_VISIBILITY}
        noRowsMessage="No allowlisted senders match this view."
        // Nothing acts on a selection here — the row actions live in the overflow
        // menu — so a highlighted row and a "1 row selected" count claimed a state
        // that meant nothing.
        disableRowSelectionOnClick
        compact
        gridSx={GRID_SX}
      />
    </Stack>
  );
}

export type { SenderListingRow };
